'use client';

import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

export interface MuxClipPlayerHandle {
  /** Seek the underlying mux-player to the given time (seconds). */
  seek: (timeSec: number) => void;
  play: () => void;
  pause: () => void;
}

interface MuxClipPlayerProps {
  playbackId: string;
  startTime: number;
  endTime?: number; // When provided, player will pause at this time
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  className?: string;
  /** Optional callback fired with currentTime (seconds) on the clip's
   *  internal interval poll. Wired here so existing callers like
   *  MomentoVideoPanel can keep their integration after we forwarded the
   *  ref typing on this component. */
  onTimeUpdate?: (timeSec: number) => void;
}

/**
 * MUX Player that plays a specific time range
 * - Starts at startTime
 * - Automatically pauses at endTime (if provided)
 * - Supports looping within the time range
 *
 * Forwards a ref with seek/play/pause so parent components can drive the
 * underlying mux-player without reaching into the DOM themselves.
 */
export const MuxClipPlayer = forwardRef<MuxClipPlayerHandle, MuxClipPlayerProps>(function MuxClipPlayer({
  playbackId,
  startTime,
  endTime,
  autoPlay = false,
  muted = false,
  loop = false,
  controls = true,
  className = '',
  onTimeUpdate,
}, ref) {
  const playerRef = useRef<HTMLDivElement>(null);
  const muxPlayerElRef = useRef<any>(null);
  const [scriptLoaded, setScriptLoaded] = React.useState(false);
  // CRITICAL: stash dynamic props in refs so the effect that creates the
  // <mux-player> element doesn't depend on them. Without this, every
  // parent re-render that passes a new inline function (or new moment
  // start_time/end_time) destroys + recreates the player. That's how we
  // got "click word → plays briefly → loops back to the moment's
  // beginning" before — and how we used to lose currentTime + the MUX
  // HLS session every time the active moment changed.
  //
  // The interval below reads endTimeRef/loopRef every tick, so toggling
  // auto-pause (endTime → undefined) or loop is instant without a
  // remount. start-time only matters at creation, but we still mirror
  // it into a ref so any future late mount uses the latest value.
  const onTimeUpdateRef = useRef(onTimeUpdate);
  onTimeUpdateRef.current = onTimeUpdate;
  const startTimeRef = useRef(startTime);
  startTimeRef.current = startTime;
  const endTimeRef = useRef(endTime);
  endTimeRef.current = endTime;
  const loopRef = useRef(loop);
  loopRef.current = loop;

  useImperativeHandle(
    ref,
    () => ({
      seek: (timeSec: number) => {
        const el = muxPlayerElRef.current;
        if (el && Number.isFinite(timeSec)) el.currentTime = timeSec;
      },
      play: () => muxPlayerElRef.current?.play?.(),
      pause: () => muxPlayerElRef.current?.pause?.(),
    }),
    [],
  );

  // Load MUX player script
  useEffect(() => {
    if (!document.querySelector('script[src*="mux-player"]')) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/@mux/mux-player@2.4.1/dist/mux-player.js';
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      document.body.appendChild(script);
    } else {
      setScriptLoaded(true);
    }
  }, []);

  // Create player when script is loaded
  useEffect(() => {
    if (!scriptLoaded || !playerRef.current || !playbackId) return;

    // Clear existing player
    playerRef.current.innerHTML = '';

    // Create mux-player element
    const muxPlayerEl = document.createElement('mux-player') as any;
    muxPlayerElRef.current = muxPlayerEl;

    // Use playback-id for standard playback
    muxPlayerEl.setAttribute('playback-id', playbackId);

    // Set start time — read via ref so a late-arriving start_time still
    // takes effect on initial mount.
    const initialStartTime = startTimeRef.current;
    if (initialStartTime !== undefined) {
      muxPlayerEl.setAttribute('start-time', initialStartTime.toString());
    }

    // Playback settings — loop is read via ref each interval tick so it
    // can be toggled without remounting; the others are creation-only.
    if (autoPlay) muxPlayerEl.setAttribute('autoplay', '');
    if (muted) muxPlayerEl.setAttribute('muted', '');
    if (loopRef.current) muxPlayerEl.setAttribute('loop', '');
    if (controls) muxPlayerEl.setAttribute('controls', '');

    // Stream type
    muxPlayerEl.setAttribute('stream-type', 'on-demand');

    // Preload for faster hover previews
    muxPlayerEl.setAttribute('preload', 'metadata');

    // Styling
    muxPlayerEl.style.width = '100%';
    muxPlayerEl.style.height = '100%';

    // Add to DOM first
    playerRef.current.appendChild(muxPlayerEl);

    // Add event listener to stop at endTime if specified, and to surface
    // currentTime to onTimeUpdate consumers.
    let intervalId: NodeJS.Timeout | null = null;
    // Always poll currentTime: feeds onTimeUpdate consumers AND enforces
    // the endTime auto-pause when set. The poll reads onTimeUpdateRef so
    // a new inline onTimeUpdate from a parent doesn't tear down the player.
    //
    // CRITICAL: only auto-pause on NATURAL playback crossing of endTime
    // (delta ≈ 0.1s per 100ms tick). If currentTime jumped (user seeked
    // outside the moment range via transcript click or scrubber), the
    // delta will be much larger — let them watch from there. Without this
    // distinction every seek past endTime got ping-ponged back to endTime
    // by the very next tick, eventually breaking MUX's HLS pipeline with
    // DEMUXER_ERROR_COULD_NOT_PARSE.
    let prevTime = -1;
    intervalId = setInterval(() => {
      if (muxPlayerEl.currentTime === undefined || muxPlayerEl.currentTime === null) {
        return;
      }
      const currentTime = muxPlayerEl.currentTime;
      onTimeUpdateRef.current?.(currentTime);

      // Read endTime/loop from refs every tick — parent can disable
      // auto-pause (endTime → undefined) or toggle loop without
      // recreating the player.
      const liveEndTime = endTimeRef.current;
      const liveLoop = loopRef.current;
      const liveStartTime = startTimeRef.current;

      if (liveEndTime !== undefined) {
        const delta = prevTime >= 0 ? currentTime - prevTime : 0;
        const naturalCross =
          prevTime >= 0 &&
          prevTime < liveEndTime &&
          currentTime >= liveEndTime &&
          delta >= 0 &&
          delta < 0.5; // 0.5s allows even 4x playback; rejects seeks
        if (naturalCross) {
          if (liveLoop) {
            muxPlayerEl.currentTime = liveStartTime ?? 0;
          } else {
            muxPlayerEl.pause();
            muxPlayerEl.currentTime = liveEndTime;
          }
        }
      }
      prevTime = currentTime;
    }, 100);

    return () => {
      // Clear the interval when component unmounts
      if (intervalId) {
        clearInterval(intervalId);
      }

      if (playerRef.current) {
        playerRef.current.innerHTML = '';
      }
    };
    // The player is created ONCE per playback session (mount + script
    // load + playbackId). startTime / endTime / loop / onTimeUpdate
    // flow through the refs above — adding them to deps would tear the
    // player down on every parent re-render and re-fetch the HLS
    // manifest, defeating the moments-mode "active moment auto-updates
    // while the player keeps rolling" UX.
  }, [scriptLoaded, playbackId, autoPlay, muted, controls]);

  if (!playbackId) {
    return (
      <div className={`${className} bg-muted flex items-center justify-center`}>
        <p className="text-muted-foreground text-sm">No video available</p>
      </div>
    );
  }

  return (
    <div
      ref={playerRef}
      className={`${className} bg-black`}
      data-testid="mux-clip-player"
    />
  );
});
